/**
 * FinancialBriefingHero — v3.7.19
 *
 * Dark-green hero variant reserved for the "Most important right now"
 * story on the Overview Dashboard. Design tokens live in
 * `index.css` under `--financial-briefing-*` and are scoped to
 * `.briefing-hero` — ordinary dashboard cards do NOT inherit them.
 *
 * Layout (desktop):
 *   ┌───────────────────────────────────────────────────────────┐
 *   │  Your financial picture           freshness · accounts    │
 *   │                                                           │
 *   │  Left column (65–70%)         │  Right impact panel (30–35%)│
 *   │  ─────────────────────────    │  ─────────────────────────  │
 *   │  MOST IMPORTANT RIGHT NOW     │  If you redirect …          │
 *   │  Big headline                 │  Reach 3 months by …        │
 *   │  Supporting body              │  About 7 weeks sooner …     │
 *   │  [ Primary CTA ] Why…         │  ▓▓▓▓▓░░░░ (gold progress)  │
 *   └───────────────────────────────────────────────────────────┘
 *
 * Impact-panel content is derived from `nba.impact` when supplied by
 * the backend, or by splitting `nba.cost_note` on newline / " · "
 * so the same UI works with existing rules-engine copy.
 *
 * The dark-green treatment is applied to all states (updates present,
 * empty / fallback, set-aside). "Also worth knowing" continues to
 * render as a separate light card below the hero — the dashboard's
 * ordinary card language is intentionally preserved for secondary
 * content, per the design spec.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Sparkles, Receipt, TrendingUp, Compass, Clock, ArrowRight as ArrowRightIcon } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import WhySeeingThis from '@/components/WhySeeingThis';

// NBA CTA opens the detail drawer first (unless the NBA explicitly
// declares `cta.action='skip-drawer'`). See Phase 84.7 notes.
function handleNbaAction(nba, opts) {
  const { onOpenDrawer, tone, updateId, navigate } = opts;
  const cta = nba && nba.cta;
  const action = (cta && cta.action) || 'navigate';
  if (action === 'skip-drawer' && cta && cta.target && navigate) {
    navigate(cta.target);
    return;
  }
  onOpenDrawer?.('next-best-action', updateId);
  void tone;
}

const ICONS = {
  receipt:   Receipt,
  'trend-up': TrendingUp,
  compass:   Compass,
  clock:     Clock,
};

// ─────────────────────────────────────────────────────────────
// Impact panel helpers
// ─────────────────────────────────────────────────────────────

/**
 * Derive the three-line impact hierarchy from an NBA.
 * Priority order:
 *   1. nba.impact = { condition, outcome, comparison, progress_pct }
 *   2. nba.cost_note split on \n or " · "
 *   3. nba.cost_note as a single "outcome" line
 * Returns null when there is nothing to show.
 */
function deriveImpact(nba) {
  if (!nba) return null;
  const impact = nba.impact && typeof nba.impact === 'object' ? nba.impact : null;
  if (impact && (impact.condition || impact.outcome || impact.comparison)) {
    return {
      condition:  impact.condition  || null,
      outcome:    impact.outcome    || null,
      comparison: impact.comparison || null,
      progressPct: Number.isFinite(impact.progress_pct) ? impact.progress_pct : null,
    };
  }
  const raw = (nba.cost_note || '').trim();
  if (!raw) return null;
  const parts = (raw.includes('\n')
    ? raw.split('\n')
    : raw.includes(' · ')
      ? raw.split(' · ')
      : [raw]
  ).map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) {
    return { condition: null, outcome: parts[0], comparison: null, progressPct: null };
  }
  if (parts.length === 2) {
    return { condition: parts[0], outcome: parts[1], comparison: null, progressPct: null };
  }
  return {
    condition:  parts[0],
    outcome:    parts[1],
    comparison: parts.slice(2).join(' '),
    progressPct: null,
  };
}

function ImpactPanel({ nba }) {
  const impact = deriveImpact(nba);
  if (!impact) return null;
  const { condition, outcome, comparison, progressPct } = impact;
  return (
    <aside
      className="briefing-hero__panel p-5 sm:p-6 h-full flex flex-col gap-3"
      data-testid="briefing-impact-panel"
      aria-label="Projected effect"
    >
      <div
        className="briefing-hero__accent text-[10px] uppercase tracking-[0.16em] font-semibold"
        data-testid="briefing-impact-eyebrow"
      >
        Projected effect
      </div>
      {condition && (
        <p
          className="briefing-hero__muted text-sm leading-snug"
          data-testid="briefing-impact-condition"
        >
          {condition}
        </p>
      )}
      {outcome && (
        <p
          className="font-fraunces text-xl sm:text-2xl leading-snug"
          style={{ color: 'var(--financial-briefing-text)' }}
          data-testid="briefing-impact-outcome"
        >
          {outcome}
        </p>
      )}
      {comparison && (
        <p
          className="briefing-hero__muted text-xs sm:text-sm leading-snug"
          data-testid="briefing-impact-comparison"
        >
          {comparison}
        </p>
      )}
      {Number.isFinite(progressPct) && (
        <div className="mt-auto pt-2" data-testid="briefing-impact-progress">
          <div className="briefing-hero__progress-track" role="progressbar" aria-valuenow={Math.round(progressPct)} aria-valuemin={0} aria-valuemax={100}>
            <div
              className="briefing-hero__progress-fill"
              style={{ width: `${Math.max(0, Math.min(100, progressPct))}%` }}
            />
          </div>
        </div>
      )}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────
// Left column: recommendation body + primary CTA
// ─────────────────────────────────────────────────────────────

function RecommendationBody({ update, nba, onOpenDrawer, updateId, onNavigate }) {
  const navigate = useNavigate();
  const [setAside, setSetAside] = useState(false);
  const whyReason = update.why || update.body_text || update.body_line || null;

  if (setAside) {
    return (
      <div data-testid="briefing-nba-set-aside">
        <div className="briefing-hero__accent text-[10px] uppercase tracking-[0.16em] font-semibold">
          Most important right now
        </div>
        <p className="font-fraunces text-2xl sm:text-3xl leading-tight mt-2">
          You&apos;ve set this aside for now.
        </p>
        <p className="briefing-hero__muted text-sm mt-3">
          2120 will surface this again when it&apos;s still relevant.
        </p>
        <Button
          variant="ghost"
          className="briefing-hero__secondary mt-4 px-0 hover:bg-transparent"
          onClick={() => setSetAside(false)}
          data-testid="briefing-nba-show-again"
        >
          Show again
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 briefing-hero__accent text-[10px] uppercase tracking-[0.16em] font-semibold" data-testid="briefing-most-important-label">
        <Sparkles className="h-3 w-3" aria-hidden="true" />
        <span>Most important right now</span>
      </div>
      <h3
        className="font-fraunces mt-3 leading-tight"
        style={{
          color: 'var(--financial-briefing-text)',
          fontSize: 'clamp(24px, 2.4vw, 32px)',
          fontWeight: 400,
        }}
        data-testid="briefing-headline"
      >
        {update.headline}
      </h3>
      {update.sub_line && (
        <p className="briefing-hero__muted text-sm sm:text-base mt-3 leading-relaxed" data-testid="briefing-subline">
          {update.sub_line}
        </p>
      )}
      {update.body_line && (
        <p className="briefing-hero__muted text-sm sm:text-base mt-2 leading-relaxed" data-testid="briefing-body">
          {update.body_line}
        </p>
      )}
      {update.priority_note && (
        <p
          className="text-xs sm:text-sm mt-3 italic leading-relaxed"
          style={{ color: 'var(--financial-briefing-accent)' }}
          data-testid="briefing-priority-note"
        >
          {update.priority_note}
        </p>
      )}
      {nba && (
        <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="briefing-nba-panel">
          <Button
            onClick={() => handleNbaAction(nba, { navigate, onOpenDrawer, tone: update.category, updateId, onNavigate })}
            className="briefing-hero__cta rounded-full h-9 px-4 font-medium"
            data-testid="briefing-nba-cta"
          >
            {nba.cta?.label || nba.action_label || 'Continue'}
            <ArrowRightIcon className="h-3.5 w-3.5 ml-1.5" aria-hidden="true" />
          </Button>
          {whyReason && (
            <WhySeeingThis
              reason={whyReason}
              testid="briefing-why"
              className="briefing-hero__secondary inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              label="Why you're seeing this"
            />
          )}
          <Button
            variant="ghost"
            className="briefing-hero__secondary hover:bg-transparent"
            onClick={() => setSetAside(true)}
            data-testid="briefing-nba-dismiss"
          >
            {nba.dismiss?.label || 'Not now'}
          </Button>
        </div>
      )}
      {!nba && whyReason && (
        <div className="mt-4">
          <WhySeeingThis
            reason={whyReason}
            testid="briefing-why"
            className="briefing-hero__secondary inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
            label="Why you're seeing this"
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Also-worth-knowing (rendered as a separate light card, below)
// ─────────────────────────────────────────────────────────────

function AlsoWorthKnowing({ items, onOpenDrawer }) {
  const navigate = useNavigate();
  if (!items || items.length === 0) return null;
  return (
    <Card className="border-border/70" data-testid="briefing-also-worth-knowing-card">
      <CardContent className="p-0">
        <div className="border-b border-border/60 px-6 py-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
            Also worth knowing
          </div>
        </div>
        <ul className="divide-y divide-border/50">
          {items.map((u, i) => {
            const Icon = ICONS[u.tab_icon] || Sparkles;
            const nba  = u.next_best_action || null;
            return (
              <li key={u.id || i}>
                <button
                  type="button"
                  onClick={() => nba && handleNbaAction(nba, { navigate, onOpenDrawer, tone: u.category, updateId: u.id })}
                  disabled={!nba}
                  className="w-full flex items-start gap-3 px-6 py-3 text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-70 disabled:cursor-default"
                  data-testid={`briefing-also-item-${i}`}
                >
                  <span className="h-8 w-8 rounded-lg grid place-items-center shrink-0 bg-muted/60 text-muted-foreground">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">
                      {u.category_label}
                    </span>
                    <span className={`block text-sm font-medium text-foreground truncate mt-0.5 ${nba ? 'group-hover:underline' : ''}`}>
                      {u.headline}
                    </span>
                    {u.sub_line && (
                      <span className="block text-xs text-muted-foreground truncate mt-0.5">{u.sub_line}</span>
                    )}
                  </span>
                  {nba && <ArrowRightIcon className="h-4 w-4 text-muted-foreground shrink-0 mt-1" aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Empty / fallback state — dark shell retained
// ─────────────────────────────────────────────────────────────

function EmptyStateBody({ keyMetrics, baseCurrency, onOpenDrawer, activationSignals, onNavigate }) {
  const nudge = computeFallbackNudge({ signals: activationSignals, hasBriefingNba: false });
  const hasMetrics = Array.isArray(keyMetrics) && keyMetrics.length > 0;
  return (
    <div className="space-y-6" data-testid="briefing-empty-body">
      {hasMetrics ? (
        <>
          <p className="briefing-hero__muted text-sm" data-testid="briefing-placeholder-caption">
            Your month at a glance. A composed briefing will appear here once your data supports one.
          </p>
          <div className="grid grid-cols-3 gap-4">
            {keyMetrics.slice(0, 3).map((km) => (
              <div key={km.key} data-testid={`briefing-metric-${km.key}`}>
                <div className="briefing-hero__muted text-[10px] uppercase tracking-[0.14em] font-medium">
                  {km.label}
                </div>
                <div className="mt-1 num text-xl font-semibold" style={{ color: 'var(--financial-briefing-text)' }}>
                  {km.value_base == null ? '—'
                    : km.unit === 'currency' ? formatMoney(km.value_base, baseCurrency)
                    : km.unit === 'percent'  ? `${Math.round(km.value_base * 10) / 10}%`
                    : km.unit === 'months'   ? `${Math.round(km.value_base * 10) / 10} mo`
                    : String(km.value_base)}
                </div>
              </div>
            ))}
          </div>
          <div>
            <Button
              variant="ghost"
              className="briefing-hero__secondary hover:bg-transparent px-0"
              onClick={() => onOpenDrawer?.('briefing')}
              data-testid="briefing-open-drawer"
            >
              Open briefing
              <ArrowRightIcon className="h-3.5 w-3.5 ml-1.5" aria-hidden="true" />
            </Button>
          </div>
        </>
      ) : (
        <>
          <div>
            <div className="briefing-hero__accent text-[10px] uppercase tracking-[0.16em] font-semibold">
              Your financial briefing
            </div>
            <p
              className="font-fraunces mt-3 leading-tight"
              style={{
                color: 'var(--financial-briefing-text)',
                fontSize: 'clamp(24px, 2.4vw, 32px)',
                fontWeight: 400,
              }}
              data-testid="briefing-empty-headline"
            >
              A clearer picture is on the way.
            </p>
            <p className="briefing-hero__muted text-sm sm:text-base mt-3 max-w-xl leading-relaxed" data-testid="briefing-empty-copy">
              Once we&rsquo;ve seen a few weeks of transactions, 2120 composes a
              plain-English briefing every day &mdash; what you spent, what
              changed, and one thing worth doing.
            </p>
          </div>
        </>
      )}
      {nudge && (
        <div className="briefing-hero__panel p-5 sm:p-6" data-testid="briefing-hero-fallback-nudge">
          <div className="briefing-hero__accent text-[10px] uppercase tracking-[0.16em] font-semibold">
            Next step
          </div>
          <p
            className="font-fraunces mt-2 leading-snug"
            style={{ color: 'var(--financial-briefing-text)', fontSize: '20px', fontWeight: 400 }}
            data-testid="briefing-hero-fallback-headline"
          >
            {nudge.headline}
          </p>
          <p className="briefing-hero__muted text-sm mt-2 leading-relaxed">{nudge.context}</p>
          <div className="mt-4">
            <Button
              onClick={() => onNavigate?.(nudge.cta.target)}
              className="briefing-hero__cta rounded-full h-9 px-4 font-medium"
              data-testid="briefing-hero-fallback-cta"
            >
              {nudge.cta.label}
              <ArrowRightIcon className="h-3.5 w-3.5 ml-1.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function computeFallbackNudge({ signals, hasBriefingNba }) {
  if (hasBriefingNba) return null;
  if (!signals) return null;
  if (signals.hasAccount === false) {
    return {
      headline: 'Add your first account to start the story.',
      context: 'Everything in 2120 — spending patterns, insights, goals — flows from your accounts. Add one and the picture fills in.',
      cta: { label: 'Add an account', target: '/accounts' },
    };
  }
  if (signals.hasImported === false) {
    return {
      headline: 'Bring in a statement so the picture fills in.',
      context: 'Drop a bank or credit-card PDF/CSV. We parse, categorise and de-duplicate — you get a real month of clarity in minutes.',
      cta: { label: 'Import a statement', target: '/import' },
    };
  }
  if (signals.hasCategorised === false) {
    return {
      headline: 'Review your first month of spend.',
      context: 'A five-minute pass over categories teaches 2120 how you actually live — every future briefing gets sharper for it.',
      cta: { label: 'Open transactions', target: '/transactions' },
    };
  }
  if (signals.hasGoal === false) {
    return {
      headline: 'Set your first goal — a direction, not a deadline.',
      context: 'Even one goal changes how every future briefing reads: your progress, your commitments and your cash-in-hand all thread through it.',
      cta: { label: 'Set a goal', target: '/goals' },
    };
  }
  if (signals.hasReflection === false) {
    return {
      headline: 'A minute of reflection sharpens the next briefing.',
      context: 'One honest sentence about the month you just had teaches 2120 what matters to you — the nudges get a lot more personal from there.',
      cta: { label: 'Reflect for a minute', target: '/reflections' },
    };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function FinancialBriefingHero({
  briefing = null,
  keyMetrics = [],
  loading = false,
  baseCurrency = 'INR',
  greetingName = null,
  onOpenDrawer,
  onNavigate,
  activationSignals = null,
  accountsIncluded = null,
}) {
  void greetingName; // header owns the greeting now
  const updates = (briefing && Array.isArray(briefing.updates)) ? briefing.updates : [];
  const secondaryUpdates = (briefing && Array.isArray(briefing.secondary_updates)) ? briefing.secondary_updates : [];
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    if (activeIdx >= updates.length) setActiveIdx(0);
  }, [updates.length, activeIdx]);

  if (loading) {
    return (
      <Card className="briefing-hero overflow-hidden" data-testid="dashboard-financial-briefing">
        <CardContent className="p-6 sm:p-7 space-y-3">
          <Skeleton className="h-4 w-40 bg-white/10" />
          <Skeleton className="h-10 w-3/4 bg-white/10" />
          <Skeleton className="h-4 w-2/3 bg-white/10" />
          <Skeleton className="h-24 w-full mt-4 bg-white/10" />
        </CardContent>
      </Card>
    );
  }

  const hasUpdates = updates.length > 0;
  const active = hasUpdates ? updates[Math.max(0, Math.min(activeIdx, updates.length - 1))] : null;
  const nba    = active?.next_best_action || null;

  // Data freshness pill copy (Updated Aug 11 / Updated 2 hours ago / etc).
  const freshness = (() => {
    const iso = briefing?.generated_at;
    if (!iso) return null;
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
      if (diffMin < 2)  return 'Updated just now';
      if (diffMin < 60) return `Updated ${diffMin} min ago`;
      const hr = Math.round(diffMin / 60);
      if (hr < 24) return `Updated ${hr} hour${hr === 1 ? '' : 's'} ago`;
      return `Updated ${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
    } catch { return null; }
  })();

  return (
    <div className="space-y-4">
      <Card className="briefing-hero overflow-hidden" data-testid="dashboard-financial-briefing">
        <CardContent className="p-6 sm:p-7">
          {/* Header row: title + right-aligned pills */}
          <div className="flex items-start justify-between gap-3 flex-wrap" data-testid="briefing-section-header">
            <h2
              className="font-fraunces leading-tight"
              style={{
                color: 'var(--financial-briefing-text)',
                fontSize: 'clamp(22px, 2.2vw, 30px)',
                fontWeight: 400,
              }}
              data-testid="briefing-hero-title"
            >
              Your financial picture
            </h2>
            <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
              {freshness && (
                <span
                  className="briefing-hero__pill inline-flex items-center gap-1.5 text-[11px] rounded-full px-2.5 py-1"
                  data-testid="briefing-freshness"
                >
                  <Clock className="h-3 w-3" aria-hidden="true" />
                  {freshness}
                </span>
              )}
              {(accountsIncluded != null && accountsIncluded > 0) && (
                <span
                  className="briefing-hero__pill inline-flex items-center gap-1.5 text-[11px] rounded-full px-2.5 py-1"
                  data-testid="briefing-accounts-included"
                >
                  {accountsIncluded} account{accountsIncluded === 1 ? '' : 's'} included
                </span>
              )}
            </div>
          </div>

          {/* Body — two-column on desktop, stacked on mobile */}
          <div className="mt-5 sm:mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(260px,34%)] gap-6 sm:gap-8" data-testid="briefing-body-grid">
            <div className="min-w-0" data-testid="briefing-body-left">
              {hasUpdates ? (
                <RecommendationBody
                  update={active}
                  nba={nba}
                  onOpenDrawer={onOpenDrawer}
                  onNavigate={onNavigate}
                  updateId={active.id}
                />
              ) : (
                <EmptyStateBody
                  keyMetrics={keyMetrics}
                  baseCurrency={baseCurrency}
                  onOpenDrawer={onOpenDrawer}
                  activationSignals={activationSignals}
                  onNavigate={onNavigate}
                />
              )}
            </div>
            <div className="min-w-0" data-testid="briefing-body-right">
              {hasUpdates && nba && <ImpactPanel nba={nba} />}
            </div>
          </div>

          {/* Compact story switcher — muted, in-hero, no competing CTA */}
          {hasUpdates && updates.length > 1 && (
            <div
              className="mt-6 pt-4 border-t border-white/10 flex items-center gap-2 flex-wrap"
              role="tablist"
              data-testid="briefing-story-switcher"
            >
              <span className="briefing-hero__muted text-[11px] uppercase tracking-[0.14em] font-medium mr-1">
                Other stories
              </span>
              {updates.map((u, i) => (
                <button
                  key={u.id || i}
                  type="button"
                  role="tab"
                  aria-selected={i === activeIdx}
                  onClick={() => setActiveIdx(i)}
                  className={
                    'text-[11px] px-2.5 py-1 rounded-full border transition-colors ' +
                    (i === activeIdx
                      ? 'bg-white/15 border-white/25 text-[var(--financial-briefing-text)] font-medium'
                      : 'bg-transparent border-white/15 text-[var(--financial-briefing-text-muted)] hover:text-[var(--financial-briefing-text)]')
                  }
                  data-testid={`briefing-story-chip-${i}`}
                >
                  {i === 0 ? 'Primary' : `#${i + 1}`}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <AlsoWorthKnowing items={secondaryUpdates} onOpenDrawer={onOpenDrawer} />
    </div>
  );
}
